/**
 * Fetch wrapper com autenticação automática
 * 
 * Este módulo fornece uma função fetch que adiciona automaticamente
 * o header de Authorization com o token da API.
 * 
 * Uso: Substitua `fetch(url, options)` por `authenticatedFetch(url, options)`
 */

import { getConfigSync, loadRuntimeConfig } from './runtimeConfig';

let configLoaded = false;

/**
 * Garante que a configuração está carregada antes de fazer requisições
 */
export async function ensureConfigLoaded(): Promise<void> {
  if (!configLoaded) {
    await loadRuntimeConfig();
    configLoaded = true;
  }
}

/**
 * Adiciona headers de autenticação às RequestInit options
 */
function addAuthHeaders(options: RequestInit = {}): RequestInit {
  const config = getConfigSync();
  
  const authHeaders: Record<string, string> = {};
  if (config.apiToken) {
    authHeaders['Authorization'] = `Token ${config.apiToken}`;
  }
  
  return {
    ...options,
    headers: {
      ...authHeaders,
      ...(options.headers || {}),
    },
  };
}

/**
 * Fetch wrapper que adiciona automaticamente o token de autenticação.
 * 
 * @param url URL da requisição
 * @param options Opções do fetch (method, headers, body, etc.)
 * @returns Promise<Response>
 * 
 * @example
 * // GET simples
 * const response = await authenticatedFetch('/api/data');
 * 
 * @example
 * // POST com JSON
 * const response = await authenticatedFetch('/api/data', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ key: 'value' })
 * });
 */
export async function authenticatedFetch(
  url: string | URL | Request,
  options: RequestInit = {}
): Promise<Response> {
  // Garantir que a config está carregada
  await ensureConfigLoaded();
  
  // Adicionar headers de autenticação
  const authenticatedOptions = addAuthHeaders(options);
  
  return fetch(url, authenticatedOptions);
}

/**
 * Versão síncrona do fetch wrapper (usa config do cache).
 * ATENÇÃO: Só funciona após ensureConfigLoaded() ter sido chamado.
 * Prefira usar authenticatedFetch() que é assíncrono.
 */
export function authenticatedFetchSync(
  url: string | URL | Request,
  options: RequestInit = {}
): Promise<Response> {
  const authenticatedOptions = addAuthHeaders(options);
  return fetch(url, authenticatedOptions);
}

/**
 * Helper para fazer GET com autenticação
 */
export async function authGet(url: string): Promise<Response> {
  return authenticatedFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Helper para fazer POST com autenticação
 */
export async function authPost(
  url: string, 
  data: object | FormData,
  isFormData = false
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  return authenticatedFetch(url, {
    method: 'POST',
    headers,
    body: isFormData ? (data as FormData) : JSON.stringify(data),
  });
}

// Re-exportar para conveniência
export { loadRuntimeConfig, getConfigSync } from './runtimeConfig';
